import React, { useCallback, useState } from 'react';
import { ReceiptText, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import MultipleItemsSection from './MultipleItemsSection';
import InvoiceDetailsSection from './InvoiceDetailsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNumber } from '../../../../../utils/validation';
import { calculateVAT } from '../../../../../utils/calculations/finance';

/**
 * Компонент основной формы расчета НДС
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  vatRates,
  accountingTypes,
  saveCalculation, 
  addToHistory
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeOtherTaxes' && !checked) {
        newState.otherTaxRate = '0';
        newState.otherTaxName = '';
      }
      
      if (name === 'showInvoiceDetails' && !checked) {
        newState.items = [];
      }
      
      // Автоматический переход на расчетную ставку при выделении НДС
      if (name === 'calculationType' && value === 'extract') {
        if (prev.taxRate === '20') {
          newState.taxRate = '20/120';
        } else if (prev.taxRate === '10') {
          newState.taxRate = '10/110';
        }
      } else if (name === 'calculationType' && value === 'add') {
        if (prev.taxRate === '20/120') {
          newState.taxRate = '20';
        } else if (prev.taxRate === '10/110') {
          newState.taxRate = '10';
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Обработчик изменения элементов в списке товаров/услуг
  const handleItemChange = useCallback((id, field, value) => {
    setFormData(prev => {
      const items = [...prev.items];
      const itemIndex = items.findIndex(item => item.id === id);
      
      if (itemIndex !== -1) {
        items[itemIndex] = { 
          ...items[itemIndex], 
          [field]: value 
        };
      }
      
      return { ...prev, items };
    });
  }, [setFormData]);

  // Добавление нового элемента в список
  const addItem = useCallback(() => {
    const newId = Date.now();
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { id: newId, description: '', amount: 0, taxRate: prev.taxRate }
      ]
    }));
  }, [setFormData]);

  // Удаление элемента из списка
  const removeItem = useCallback((id) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      amount, taxRate, calculationType,
      includeOtherTaxes, otherTaxRate, otherTaxName,
      items, showInvoiceDetails
    } = formData;

    // Валидация суммы если не используется режим множественных товаров
    if (!showInvoiceDetails || items.length === 0) {
      const numAmount = parseFloatOrDefault(amount, -1);
      if (numAmount <= 0) newErrors.amount = 'Сумма должна быть положительной.';
      if (!newErrors.amount && numAmount > 1000000000) newErrors.amount = 'Максимальная сумма 1 млрд ₽.';
    }
    
    // Валидация ставки НДС
    if (!taxRate) newErrors.taxRate = 'Выберите ставку НДС.';
    if (taxRate !== 'exempt' && calculationType === 'extract' && 
        taxRate !== '20/120' && taxRate !== '10/110') {
      newErrors.taxRate = 'Для выделения НДС используйте расчетные ставки 20/120 или 10/110.';
    }
    
    // Валидация типа расчета
    if (!calculationType) newErrors.calculationType = 'Выберите тип расчета.';
    
    // Валидация дополнительного налога, если включен
    if (includeOtherTaxes) {
      const numOtherTaxRate = parseFloatOrDefault(otherTaxRate, -1);
      if (numOtherTaxRate < 0) newErrors.otherTaxRate = 'Ставка налога не может быть отрицательной.';
      if (numOtherTaxRate > 100) newErrors.otherTaxRate = 'Ставка налога не может быть более 100%.';
      if (!otherTaxName.trim()) newErrors.otherTaxName = 'Укажите название налога.';
    }
    
    // Валидация элементов, если используется режим множественных товаров
    if (showInvoiceDetails && items.length > 0) {
      const itemErrors = {};
      items.forEach(item => {
        const itemAmount = parseFloatOrDefault(item.amount, -1);
        if (itemAmount <= 0) {
          if (!itemErrors[item.id]) itemErrors[item.id] = {};
          itemErrors[item.id].amount = 'Сумма должна быть положительной.';
        }
        if (!item.description.trim()) {
          if (!itemErrors[item.id]) itemErrors[item.id] = {};
          itemErrors[item.id].description = 'Укажите наименование.';
        }
      });
      if (Object.keys(itemErrors).length > 0) {
        newErrors.items = itemErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета НДС
  const calculateTax = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Выполняем расчеты ---
        const { amount, taxRate, calculationType, includeOtherTaxes, otherTaxRate, items, showInvoiceDetails } = formData;
        
        let results;

        // Обработка разных случаев расчета
        if (showInvoiceDetails && items.length > 0) {
          // Расчет для множества товаров/услуг
          const calculatedItems = items.map(item => {
            const itemAmount = parseFloatOrDefault(item.amount, 0);
            
            // Определяем, включен ли НДС в сумму
            const includesVat = calculationType === 'extract';
            
            // Расчет НДС для текущего элемента
            let itemTaxRate;
            if (item.taxRate === 'exempt') {
              itemTaxRate = 0;
            } else if (item.taxRate === '20/120') {
              itemTaxRate = 20;
            } else if (item.taxRate === '10/110') {
              itemTaxRate = 10;
            } else {
              itemTaxRate = parseFloatOrDefault(item.taxRate, 0);
            }
            
            const vatResult = calculateVAT(itemAmount, itemTaxRate, includesVat);
            
            return {
              id: item.id,
              description: item.description,
              taxRate: item.taxRate,
              amountWithoutVAT: vatResult.amountWithoutVAT,
              vatAmount: vatResult.vatAmount,
              amountWithVAT: vatResult.amountWithVAT,
              isExempt: item.taxRate === 'exempt'
            };
          });
          
          // Суммируем результаты
          const totalWithoutVAT = calculatedItems.reduce((sum, item) => sum + item.amountWithoutVAT, 0);
          const totalVATAmount = calculatedItems.reduce((sum, item) => sum + item.vatAmount, 0);
          const totalWithVAT = calculatedItems.reduce((sum, item) => sum + item.amountWithVAT, 0);
          
          // Расчет дополнительного налога, если включен
          let otherTaxAmount = 0;
          if (includeOtherTaxes) {
            const otherTaxRateValue = parseFloatOrDefault(otherTaxRate, 0);
            otherTaxAmount = totalWithVAT * (otherTaxRateValue / 100);
          }
          
          results = {
            items: calculatedItems,
            totalAmountWithoutVAT: totalWithoutVAT,
            totalVATAmount,
            totalAmountWithVAT: totalWithVAT,
            otherTaxAmount,
            totalPayable: totalWithVAT + otherTaxAmount,
            calculationType,
            isMultipleItems: true
          };
        } else {
          // Расчет для одного значения
          const numAmount = parseFloatOrDefault(amount, 0);
          
          // Определяем, включен ли НДС в сумму
          const includesVat = calculationType === 'extract';
          
          // Определяем ставку НДС
          let numTaxRate;
          if (taxRate === 'exempt') {
            numTaxRate = 0;
          } else if (taxRate === '20/120') {
            numTaxRate = 20;
          } else if (taxRate === '10/110') {
            numTaxRate = 10;
          } else {
            numTaxRate = parseFloatOrDefault(taxRate, 0);
          }
          
          // Расчет НДС
          const vatResult = calculateVAT(numAmount, numTaxRate, includesVat);
          
          // Расчет дополнительного налога, если включен
          let otherTaxAmount = 0;
          if (includeOtherTaxes) {
            const otherTaxRateValue = parseFloatOrDefault(otherTaxRate, 0);
            otherTaxAmount = vatResult.amountWithVAT * (otherTaxRateValue / 100);
          }
          
          results = {
            amountWithoutVAT: vatResult.amountWithoutVAT,
            vatAmount: vatResult.vatAmount,
            amountWithVAT: vatResult.amountWithVAT,
            otherTaxAmount,
            totalPayable: vatResult.amountWithVAT + otherTaxAmount,
            isExempt: taxRate === 'exempt',
            calculationType,
            taxRateOriginal: taxRate, // Сохраняем оригинальную ставку для отображения
            isMultipleItems: false
          };
        }
        
        // Добавляем дополнительную информацию в результаты
        results.date = new Date().toISOString();
        results.formData = { ...formData };
        
        setResults(results);
        
        // Добавляем результат в историю
        addToHistory({
          id: Date.now().toString(),
          date: new Date().toISOString(),
          amount: formData.calculationType === 'add' 
            ? (results.isMultipleItems ? results.totalAmountWithoutVAT : results.amountWithoutVAT)
            : (results.isMultipleItems ? results.totalAmountWithVAT : results.amountWithVAT),
          taxRate: formData.taxRate,
          vatAmount: results.isMultipleItems ? results.totalVATAmount : results.vatAmount,
          calculationType: formData.calculationType
        });
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, addToHistory]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      amount: '10000',
      taxRate: '20',
      calculationType: 'add',
      includeOtherTaxes: false,
      otherTaxRate: '0',
      otherTaxName: '',
      items: [],
      regionCode: '',
      useDefaultRates: true,
      showInvoiceDetails: false,
      accountingType: 'general',
      dateOfOperation: new Date().toISOString().split('T')[0],
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Подготовка данных для экспорта
    const headers = ['Наименование', 'Сумма без НДС (₽)', 'НДС (₽)', 'Сумма с НДС (₽)'];
    if (formData.includeOtherTaxes) {
      headers.push(`${formData.otherTaxName || 'Другой налог'} (₽)`, 'Итого к оплате (₽)');
    }

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    let rows = [];
    if (results.isMultipleItems) {
      // Данные для множественных товаров
      rows = results.items.map(item => {
        const row = [
          item.description,
          formatCsvNumber(item.amountWithoutVAT),
          item.isExempt ? 'Без НДС' : formatCsvNumber(item.vatAmount),
          formatCsvNumber(item.amountWithVAT)
        ];
        
        if (formData.includeOtherTaxes) {
          const itemOtherTax = (item.amountWithVAT / results.totalAmountWithVAT) * results.otherTaxAmount;
          row.push(
            formatCsvNumber(itemOtherTax),
            formatCsvNumber(item.amountWithVAT + itemOtherTax)
          );
        }
        
        return row.join(';');
      });
      
      // Добавление итоговой строки
      const totalRow = [
        'ИТОГО:',
        formatCsvNumber(results.totalAmountWithoutVAT),
        formatCsvNumber(results.totalVATAmount),
        formatCsvNumber(results.totalAmountWithVAT)
      ];
      
      if (formData.includeOtherTaxes) {
        totalRow.push(
          formatCsvNumber(results.otherTaxAmount),
          formatCsvNumber(results.totalPayable)
        );
      }
      
      rows.push(totalRow.join(';'));
    } else {
      // Данные для одиночного расчета
      const row = [
        'Основная сумма',
        formatCsvNumber(results.amountWithoutVAT),
        results.isExempt ? 'Без НДС' : formatCsvNumber(results.vatAmount),
        formatCsvNumber(results.amountWithVAT)
      ];
      
      if (formData.includeOtherTaxes) {
        row.push(
          formatCsvNumber(results.otherTaxAmount),
          formatCsvNumber(results.totalPayable)
        );
      }
      
      rows.push(row.join(';'));
    }

    // Добавление заголовка с информацией о расчете
    const infoRows = [
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      ['Тип расчета:', formData.calculationType === 'add' ? 'Начисление НДС' : 'Выделение НДС'],
      ['Ставка НДС:', `${formData.taxRate === 'exempt' ? 'Без НДС' : formData.taxRate + '%'}`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_ндс.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTax();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <ReceiptText className="w-6 h-6 text-indigo-500" />
          Расчет НДС
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры расчета */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          vatRates={vatRates}
        />

        {/* Дополнительные параметры */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          accountingTypes={accountingTypes}
        />

        {/* Детализация по товарам, если включена */}
        {formData.showInvoiceDetails && (
          <MultipleItemsSection 
            formData={formData}
            errors={errors}
            items={formData.items}
            handleItemChange={handleItemChange}
            addItem={addItem}
            removeItem={removeItem}
            vatRates={vatRates}
          />
        )}

        {/* Данные для счета-фактуры, если включены */}
        {formData.showInvoiceDetails && (
          <InvoiceDetailsSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<ReceiptText className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Выполняется расчет...' : 'Рассчитать НДС'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;